/**
 * BM-H397 - which joint clients the conflict search actually covered.
 *
 * Mirrors backend/hub/conflict_joint_coverage.py exactly, so the screen can
 * say who is missing before the fee earner clicks, rather than the server
 * refusing afterwards with a message nobody expected.
 *
 * Rules, same as the server:
 *  - the query is split on ";" and each part compared whole
 *  - the whole query string also counts, so free text naming both people works
 *  - the primary client name counts as searched
 *  - a query part contained IN a name proves nothing ("Peter" is not
 *    "Peter Fairweather")
 */

export const normaliseName = (name) =>
  String(name == null ? "" : name).trim().toLowerCase().replace(/\s+/g, " ");

export const jointPartyNames = (jointClients) =>
  (jointClients || [])
    .map((jc) => (typeof jc === "string" ? jc : (jc && jc.name) || ""))
    .map((n) => String(n).trim())
    .filter((n) => n.length > 0);

export const searchedNames = (searchQuery, clientName) => {
  const parts = String(searchQuery || "")
    .split(";")
    .map((p) => normaliseName(p))
    .filter((p) => p.length >= 2);
  const whole = normaliseName(searchQuery);
  if (whole && !parts.includes(whole)) parts.push(whole);
  const primary = normaliseName(clientName);
  if (primary && !parts.includes(primary)) parts.push(primary);
  return parts.filter(Boolean);
};

export const uncoveredJointClients = (jointClients, searchQuery, clientName) => {
  const covered = searchedNames(searchQuery, clientName);
  return jointPartyNames(jointClients).filter((name) => {
    const target = normaliseName(name);
    if (target.length < 2) return false;
    const isCovered = covered.some(
      (part) => part === target || part.includes(target)
    );
    return !isCovered;
  });
};

/** Plain sentence naming who has not been searched, or "" when all are. */
export const uncoveredMessage = (names) => {
  if (!names || names.length === 0) return "";
  if (names.length === 1) {
    return `${names[0]} is a client on this matter but has not been searched. Add the name to the search and run it again before recording the clearance.`;
  }
  const who = `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
  return `${who} are clients on this matter but have not been searched. Add the names to the search and run it again before recording the clearance.`;
};
